import { Theme, css, useTheme } from '@emotion/react';

import { useTranslation } from 'next-i18next';

import { useMemo, useState } from 'react';

import { Sizes, Spacing, Typography } from 'chatie-design-system';
import {
  Comment,
  Heart,
  HeartFill,
  Icon,
  PinFill,
  VerticalEllipsis,
} from 'chatie-design-system/icon';

import ActionSheet, { ActionSheetOption } from '@components/common/ActionSheet';
import ImageCarousel from '@components/common/ImageCarousel';
import ImageModal from '@components/modal/ImageModal';

import { media } from '@styles/css';

import { ICommunityPost } from '../../model';
import { getPostImages } from '../../utils/community';
import { getCommunityTagNamesFromTags } from '../../utils/communityTags';
import { formatDate } from '../../utils/date';
import { trimStringByLine } from '../../utils/misc';
import Avatar, { AvatarSize } from '../common/Avatar';
import Nickname from '../user/Nickname';
import CommunityTagList from './CommunityTagList';
import MentionCards from './MentionCards';
import MentionText from './MentionText';
import PollDisplay from './PollDisplay';

const postCSS = css`
  position: relative;
`;

const postFixedCSS = (theme: Theme) => css`
  ${Typography.body12};
  color: ${theme.var.gray600};
`;

const postUserCSS = (theme: Theme) =>
  css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${Spacing.medium}px;

    .profile {
      display: flex;
      align-items: center;
    }

    .nickname {
      margin-left: ${Spacing.xsmall}px;

      .fixedContainer {
        display: flex;
        align-items: center;
        gap: ${Spacing.xxsmall}px;
      }

      small {
        display: block;
        margin-top: ${Spacing.xxsmall}px;
        color: ${theme.var.gray500};
        ${Typography.body12};
      }
    }
  `;

const postContentCSS = css`
  ${Typography.body14}
`;

const postTagListCSS = css`
  margin-top: ${Spacing.xsmall}px;
`;

const postContentMoreBtnCSS = (theme: Theme) => css`
  color: ${theme.var.gray500};

  &:before {
    content: '\\00a0';
  }
`;

const postPictureCSS = css`
  margin-top: ${Spacing.small}px;

  ${media.tablet} {
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
    border-radius: 12px;
    overflow: hidden;
  }
  ${media.desktop} {
    max-width: 480px;
    border-radius: 16px;
  }
`;

const postButtonCSS = (theme: Theme) => css`
  margin-top: ${Spacing.xsmall}px;
  color: ${theme.var.gray500};

  button {
    ${Typography.body12};
  }

  button + button {
    margin-left: ${Spacing.xsmall}px;
  }
`;

export type PostCardAction = 'modify' | 'delete' | 'report';

export type PostProps = {
  clip?: boolean;
  isMyPage?: boolean;
  onLike?: (post: ICommunityPost) => void;
  handleActionSheet?: (value: PostCardAction, id: number) => void;
  onPollVote?: (post: ICommunityPost) => void;
  onTagClick?: (tag: string) => void;
} & ICommunityPost;

const Post = ({
  clip: isClip = false,
  onLike,
  isMyPage,
  handleActionSheet,
  onPollVote,
  onTagClick,
  ..._post
}: PostProps) => {
  const post = _post as ICommunityPost;
  const {
    id,
    user,
    content,
    likeCount,
    commentCount,
    isFixed,
    isLiked,
    created,
  } = post;

  const { t } = useTranslation();
  const theme = useTheme();

  const [isContentOpen, setContentOpen] = useState(false);
  const [isImageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalIndex, setImageModalIndex] = useState(0);

  const postImages = useMemo(() => getPostImages(post), [post]);
  const hasImages = postImages.length > 0;

  const [content1, content2] = useMemo(
    () => trimStringByLine(content),
    [content]
  );
  const displayTags = useMemo(
    () => getCommunityTagNamesFromTags(post.tags),
    [post.tags]
  );

  const [actions] = useState<ActionSheetOption<PostCardAction>[]>(
    isMyPage
      ? ([
          { label: t('수정하기'), value: 'modify' },
          { label: t('삭제하기'), value: 'delete', isDanger: true },
        ] as ActionSheetOption<PostCardAction>[])
      : [{ label: t('신고하기'), value: 'report' }]
  );

  return (
    <article css={postCSS}>
      <div css={postUserCSS}>
        <div className="profile">
          <Avatar size={AvatarSize.medium} {...user} />
          <div className="nickname">
            <div className="fixedContainer">
              <h6>
                <Nickname {...user} />
              </h6>
              {isFixed && (
                <p css={postFixedCSS} className="icon-text">
                  <Icon
                    icon={PinFill}
                    size={Sizes.small}
                    color={theme.var.gray600}
                  />
                  <span>{t('고정됨')}</span>
                </p>
              )}
            </div>
            <small>{formatDate(created)}</small>
          </div>
        </div>
        {handleActionSheet && (
          <ActionSheet
            name={`post-card-action-${post.id}`}
            options={actions}
            onSelect={(value) => handleActionSheet(value, id)}
          >
            {(open) => (
              <button className="icon-btn" onClick={open}>
                <Icon
                  icon={VerticalEllipsis}
                  color={theme.var.gray400}
                  size={Sizes.small}
                />
              </button>
            )}
          </ActionSheet>
        )}
      </div>

      {isClip ? (
        <p css={postContentCSS} className="multiline">
          <MentionText content={content1} />
          {isContentOpen && <MentionText content={content2} />}
          {!isContentOpen && content2 && (
            <button
              css={postContentMoreBtnCSS}
              className="text-btn"
              onClick={(e) => {
                e.stopPropagation();
                setContentOpen(true);
              }}
            >
              ...{t('더보기')}
            </button>
          )}
        </p>
      ) : (
        <p css={postContentCSS} className="multiline">
          <MentionText content={content} />
        </p>
      )}
      <MentionCards content={content} />
      {!!displayTags.length && (
        <div css={postTagListCSS}>
          <CommunityTagList tags={displayTags} onTagClick={onTagClick} />
        </div>
      )}
      {post.poll && (
        <PollDisplay
          poll={post.poll}
          communityId={id}
          onVote={(updatedPoll) => {
            onPollVote?.({ ...post, poll: updatedPoll });
          }}
        />
      )}

      {hasImages && (
        <div css={postPictureCSS} onClick={(e) => e.stopPropagation()}>
          <ImageCarousel
            images={postImages}
            onClickImage={(index) => {
              setImageModalIndex(index);
              setImageModalOpen(true);
            }}
          />
        </div>
      )}

      <div css={postButtonCSS}>
        <button
          className="text-btn icon-text"
          onClick={(e) => {
            e.stopPropagation();
            onLike?.(post);
          }}
        >
          <Icon
            icon={isLiked ? HeartFill : Heart}
            size={Sizes.small}
            color={isLiked ? theme.colors.red500 : theme.var.gray500}
          />
          <span>{likeCount != null && likeCount > 0 ? likeCount : t('좋아요')}</span>
        </button>
        <button className="text-btn icon-text">
          <Icon icon={Comment} size={Sizes.small} color={theme.var.gray500} />
          <span>{commentCount != null && commentCount > 0 ? commentCount : t('댓글달기')}</span>
        </button>
      </div>
      {/* 이미지 확대 모달 */}
      {hasImages && (
        <ImageModal
          images={postImages}
          initialIndex={imageModalIndex}
          open={isImageModalOpen}
          onClose={() => setImageModalOpen(false)}
        />
      )}
    </article>
  );
};

export default Post;
